import { DateTime } from 'luxon';
import { SearchParam } from 'components/search/search.context';
import { makeQueryParam } from 'components/search/search.hooks';
import { getClientAlias } from 'util/client-alias';

export const dateXAxis = (xlabels: string[]) => ({
  scaleType: 'point' as const,
  data: xlabels ?? [],
  valueFormatter: (value: string, { location }: { location: string }) =>
    location === 'tooltip'
      ? DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
      : DateTime.fromISO(value).toFormat('MMM dd'),
});

type getFileNameParams = {
  startDate: string;
  endDate: string;
};

export const getFileName = (
  { startDate, endDate }: getFileNameParams,
  type:
    | 'OVERVIEW'
    | 'MESSAGES'
    | 'OFFERS'
    | 'ORDERS'
    | 'GIFT_CARDS'
    | 'SECURITY',
) => {
  let fileNamePrefix;

  switch (type) {
    case 'OVERVIEW':
      fileNamePrefix = 'Incentivio_Email_Insights_Overview';
      break;
    case 'MESSAGES':
      fileNamePrefix = 'Incentivio_Email_Insights_Messages';
      break;
    case 'OFFERS':
      fileNamePrefix = 'Incentivio_Email_Insights_Offers';
      break;
    case 'ORDERS':
      fileNamePrefix = 'Incentivio_Email_Insights_Orders';
      break;
    case 'GIFT_CARDS':
      fileNamePrefix = 'Incentivio_Email_Insights_Gift_Cards';
      break;
    case 'SECURITY':
      fileNamePrefix = 'Incentivio_Email_Insights_Security_And_Access';
      break;
  }

  return `${fileNamePrefix}_${getClientAlias()}_${DateTime.fromISO(
    startDate,
  ).toISODate()}_to_${DateTime.fromISO(endDate).toISODate()}`;
};

/**
 * Transform email pulse data grid response to include IDs and total row
 * @param response - API response containing data/rows and optional total
 * @param idField - Field name to use as the row ID (defaults to 'id')
 * @returns Array of rows with IDs, with total row appended if present
 */
export const transformEmailPulseDataGridResponse = <
  T extends Record<string, any>,
>(
  response: { data?: T[]; rows?: T[]; total?: Partial<T> } | undefined,
  idField: keyof T,
  titleField: keyof T,
): (T & { id: string | number })[] => {
  // Handle both response.data and response.rows formats
  const dataArray = response?.data ?? response?.rows;

  if (!dataArray) return [];

  // Add total row with id for grid identification
  const totalRow = response.total
    ? ({ ...response.total, id: 'total', [titleField]: 'Total' } as T & {
        id: string | number;
      })
    : null;

  // Add id to each row using the specified field
  const rowsWithId = dataArray.map(row => ({
    ...row,
    id: row[idField],
  }));

  return totalRow ? [...rowsWithId, totalRow] : rowsWithId;
};

/**
 * Transform search parameters into API request body format
 * Converts search params to query params with proper handling of arrays and ALL values
 * @param searchParams - Search parameters object containing filter values
 * @returns Query params object formatted for API requests
 */
export const makeParamsBody = (searchParams: {
  [key: string]: SearchParam;
}) => {
  const queryParams: any = {};

  // Pass through dates as-is (required fields)
  queryParams.startdate = makeQueryParam(searchParams.startdate, searchParams);
  queryParams.enddate = makeQueryParam(searchParams.enddate, searchParams);

  // storeIds array
  if (searchParams.storeIds) {
    const storeIds = makeQueryParam(searchParams.storeIds, searchParams);
    if (storeIds && storeIds !== 'ALL') {
      queryParams.storeIds = storeIds.split(',').filter(Boolean);
    }
  }

  // campaignIds array
  if (searchParams.campaignIds) {
    const campaignIds = makeQueryParam(searchParams.campaignIds, searchParams);
    if (campaignIds && campaignIds !== 'ALL') {
      queryParams.campaignIds = campaignIds.split(',').filter(Boolean);
    }
  }

  // messageIds array
  if (searchParams.messageIds) {
    const messageIds = makeQueryParam(searchParams.messageIds, searchParams);
    if (messageIds && messageIds !== 'ALL') {
      queryParams.messageIds = messageIds.split(',').filter(Boolean);
    }
  }

  // offerIds array
  if (searchParams.offerIds) {
    const offerIds = makeQueryParam(searchParams.offerIds, searchParams);
    if (offerIds && offerIds !== 'ALL') {
      queryParams.offerIds = offerIds.split(',').filter(Boolean);
    }
  }

  // status
  if (searchParams.status) {
    const status = makeQueryParam(searchParams.status, searchParams);
    if (status && status !== 'ALL') {
      queryParams.status = status;
    }
  }

  // emailType
  if (searchParams.emailType) {
    const emailType = makeQueryParam(searchParams.emailType, searchParams);
    if (emailType && emailType !== 'ALL') {
      queryParams.emailType = emailType;
    }
  }

  return queryParams;
};
